import { useEffect } from "react";
import { useRouteError } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { logApplicationFailure } from "@/lib/user-error";

/**
 * Containment for a render-path throw.
 *
 * The SPA had no boundary of any kind, so ONE bad value could blank the whole app: a fractional
 * `teamSize` in a match design made `parseDesign` throw on every render of that match, and because the
 * editor never rendered, the value could not be corrected from the UI either. The value is fixed at the
 * source and healed on read now, but the class of bug — a parser on the render path — stays possible, and
 * a white screen is never the right answer to it.
 *
 * Mounted on a PATHLESS layout route inside AppShell rather than on the "/" route itself. React renders
 * the nearest boundary in place of that route's element, so putting it on "/" replaced AppShell and took
 * the topbar and navigation down with the page - the opposite of the intent. Inside the outlet, a page
 * throw replaces only the page and the user can navigate away instead of reloading. router.tsx keeps a
 * second copy on "/" as the shell-level fallback for an AppShell or requireAuth failure.
 */
export function RouteErrorBoundary() {
  // Read for the effect's dependency only: the thrown value is a developer detail and never reaches
  // the screen, so nothing here renders it.
  const error = useRouteError();
  const intl = useIntl();

  // In an effect, not the render body: this screen re-renders on a locale switch, a theme toggle or
  // any parent update, and logging inline sent a duplicate diagnostic for every one of them. Keyed on
  // the error so a genuinely new failure still logs.
  useEffect(() => {
    logApplicationFailure("route-render", { level: "error" });
  }, [error]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-16" role="alert" data-testid="route-error">
      <h1 className="font-serif text-2xl text-ink">
        <FormattedMessage id="error.route.title" defaultMessage="This page could not be displayed" />
      </h1>
      <p className="field-value mt-3 text-ink/55">
        <FormattedMessage
          id="error.route.body"
          defaultMessage="Something in this view could not be read. Your data is unchanged. You can go back and try another page."
        />
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => window.history.back()}>
          <FormattedMessage id="common.back" defaultMessage="Back" />
        </Button>
        <Button
          variant="primary"
          onClick={() => window.location.reload()}
          aria-label={intl.formatMessage({ id: "error.route.reload", defaultMessage: "Reload the page" })}
        >
          <FormattedMessage id="error.route.reload" defaultMessage="Reload the page" />
        </Button>
      </div>
    </div>
  );
}
